/**
 * ChatToolsStrip — Always-visible strip showing active tools, connectors, skills, agents
 *
 * Strip: thin bar with colored chips for active items.
 *   - Click a chip's `x` to disable that item for THIS chat.
 *   - The "Configure" dropdown opens a two-level popover:
 *       Level 1: Category list (Web Search toggle, Connectors, Agents, Skills with counts)
 *       Level 2: Drill into a category to toggle individual items
 *
 * PR-AAU-D1 (Ask Argus Pass 1):
 *   Toggle state is now persisted per-conversation via
 *     PATCH /conversations/:id/attached-tools
 *   Hydrated on conversation switch via
 *     GET  /conversations/:id/attached-tools
 *   When `conversationId` is null (brand-new chat before first send),
 *   state stays local and is flushed once the conversation is created.
 *
 *   Founder decision (Option C hybrid, locked 2026-05-12):
 *     - Agent author sets the default at agent-config level.
 *     - New chats start with those defaults (the GET resolves them).
 *     - User adjusts per-chat freely — those overrides land in the
 *       conversation row.
 *
 * Used in: main chat, project chat.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  X, Globe, ChevronDown, ChevronRight, ArrowLeft,
  Settings2, Plug, Bot, Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/auth-context';
import { toast } from 'sonner';

// ── Types ───────────────────────────────────────────────────────────────────

// connector-status-truth (bug #3): connectionState tells the chip whether the
// connector is GENUINELY usable. Only 'connected' may be green/auto-attached;
// 'needsSetup' shows an amber "sign in" chip; 'unavailable' is hidden.
type ConnState = 'connected' | 'needsSetup' | 'unavailable';
interface Connector { id: string; name: string; icon: string; toolCount: number; enabled: boolean; connectionState: ConnState; needsSetupReason: string }
interface Agent { id: string; name: string; icon: string; description: string; enabled: boolean }
interface Skill { id: string; name: string; enabled: boolean }

export interface ChatToolsState {
  webSearch: boolean;
  connectors: Connector[];
  agents: Agent[];
  skills: Skill[];
}

// PR-AAU-D1 — wire shape for /conversations/:id/attached-tools
interface AttachedToolItem {
  id: string;
  kind: 'connector' | 'agent' | 'skill' | 'builtin';
  enabled: boolean;
}
interface AttachedToolsResponse {
  conversationId: string;
  attached: AttachedToolItem[];
  source?: 'conversation' | 'agent-default';
}

// Built-in tool ids (kept narrow — currently just web_search). Anything
// not matching a known builtin id is ignored when hydrating.
const BUILTIN_WEB_SEARCH = 'builtin:web_search';

/**
 * Flatten ChatToolsState into the wire shape. Connectors / agents /
 * skills carry their enable flag verbatim. Web Search is encoded as
 * a single builtin entry — only serialized when it's ON, so the
 * absence of the row means "off" without needing a tombstone.
 */
function stateToWire(state: ChatToolsState): AttachedToolItem[] {
  const wire: AttachedToolItem[] = [];
  if (state.webSearch) {
    wire.push({ id: BUILTIN_WEB_SEARCH, kind: 'builtin', enabled: true });
  }
  for (const c of state.connectors) {
    wire.push({ id: c.id, kind: 'connector', enabled: c.enabled });
  }
  for (const a of state.agents) {
    wire.push({ id: a.id, kind: 'agent', enabled: a.enabled });
  }
  for (const s of state.skills) {
    wire.push({ id: s.id, kind: 'skill', enabled: s.enabled });
  }
  return wire;
}

/**
 * Apply a wire payload on top of a reference state. Only the
 * `enabled` flag is taken from wire — names/icons/descriptions stay
 * authoritative on the frontend (they come from the connectors / agents
 * /skills catalogs). Wire entries that don't match a known item are
 * dropped silently — this keeps the UI stable when a connector is
 * removed at the tenant level but its id still sits in the row.
 */
function mergeWireIntoState(
  reference: ChatToolsState,
  wire: AttachedToolItem[],
): ChatToolsState {
  const byId = new Map(wire.map((w) => [w.id, w]));
  return {
    webSearch: byId.get(BUILTIN_WEB_SEARCH)?.enabled ?? false,
    connectors: reference.connectors.map((c) =>
      byId.has(c.id) ? { ...c, enabled: byId.get(c.id)!.enabled } : c,
    ),
    agents: reference.agents.map((a) =>
      byId.has(a.id) ? { ...a, enabled: byId.get(a.id)!.enabled } : a,
    ),
    skills: reference.skills.map((s) =>
      byId.has(s.id) ? { ...s, enabled: byId.get(s.id)!.enabled } : s,
    ),
  };
}

// Wire shapes from /connections-module/{mcp,api}
interface McpServerDTO {
  id: string;
  displayName: string;
  description: string;
  toolCount: number;
  isHealthy: boolean;
  connectionState?: ConnState;
  isConnected?: boolean;
  needsSetupReason?: string;
}
interface ApiConnectionDTO {
  id: string;
  displayName: string;
  description: string;
  endpointCount: number;
  isHealthy: boolean;
  connectionState?: ConnState;
  isConnected?: boolean;
  needsSetupReason?: string;
}

const EMPTY_STATE: ChatToolsState = {
  webSearch: false,
  connectors: [],
  agents: [],
  skills: [],
};

type DrillView = 'main' | 'connectors' | 'agents' | 'skills';

// ── Component ───────────────────────────────────────────────────────────────

interface ChatToolsStripProps {
  className?: string;
  /** Right-side action slot (e.g., panel toggle button) */
  rightAction?: React.ReactNode;
  /**
   * PR-AAU-D1 — When set, the strip hydrates from
   * `GET /conversations/:id/attached-tools` on mount / change AND
   * persists every toggle via `PATCH /conversations/:id/attached-tools`
   * (debounced 200ms). When null (brand-new chat before first send),
   * state stays local and the parent should re-mount the strip with the
   * real conversation id once it's available so hydration+persistence
   * can begin.
   */
  conversationId?: string | null;
}

export function ChatToolsStrip({ className, rightAction, conversationId }: ChatToolsStripProps) {
  const navigate = useNavigate();
  // bug #8 — gate the connections reads on auth being hydrated. The strip
  // mounts concurrently with the AuthProvider bootstrap, so firing these reads
  // before a token exists 401s and spams the console. Mirrors the memories-rail
  // auth-gate (isMemoryQueryEnabled = !authLoading && !!userId).
  const { user, isLoading: authLoading } = useAuth();
  const authReady = !authLoading && !!user.id;
  // ── Pull real connections from the backend ────────────────────────────
  // We surface connectors as the union of MCP servers + API connections
  // configured for the tenant. Agents and skills are NOT rendered here
  // anymore — agents are picked in the chat input, and there is no
  // tenant-level "skill toggles" surface yet (Wave 4).
  const mcpQuery = useQuery<{ servers: McpServerDTO[] }>({
    queryKey: ['connections-module', 'mcp'],
    queryFn: () =>
      apiClient.get<{ servers: McpServerDTO[] }>('/connections-module/mcp'),
    enabled: authReady,
    retry: false,
  });
  const apiQuery = useQuery<{ connections: ApiConnectionDTO[] }>({
    queryKey: ['connections-module', 'api'],
    queryFn: () =>
      apiClient.get<{ connections: ApiConnectionDTO[] }>('/connections-module/api'),
    enabled: authReady,
    retry: false,
  });

  const realConnectors: Connector[] = useMemo(() => {
    // connector-status-truth (bug #3): a connector only auto-enables (green +
    // attached) when it is GENUINELY usable (connectionState === 'connected').
    // 'unavailable' connectors are dropped entirely; 'needsSetup' show as a
    // distinct amber "sign in" chip and can't be attached.
    const stateOf = (s: { connectionState?: ConnState; isConnected?: boolean; isHealthy: boolean }): ConnState =>
      s.connectionState ?? (s.isConnected ?? s.isHealthy ? 'connected' : 'needsSetup');
    const mcp = (mcpQuery.data?.servers ?? [])
      .map((s): Connector => {
        const cs = stateOf(s);
        return {
          id: `mcp:${s.id}`,
          name: s.displayName,
          icon: '🔌',
          toolCount: s.toolCount,
          enabled: cs === 'connected',
          connectionState: cs,
          needsSetupReason: s.needsSetupReason ?? 'ok',
        };
      })
      .filter((c) => c.connectionState !== 'unavailable');
    const api = (apiQuery.data?.connections ?? [])
      .map((c): Connector => {
        const cs = stateOf(c);
        return {
          id: `api:${c.id}`,
          name: c.displayName,
          icon: '🌐',
          toolCount: c.endpointCount,
          enabled: cs === 'connected',
          connectionState: cs,
          needsSetupReason: c.needsSetupReason ?? 'ok',
        };
      })
      .filter((c) => c.connectionState !== 'unavailable');
    return [...mcp, ...api];
  }, [mcpQuery.data, apiQuery.data]);

  const [state, setState] = useState<ChatToolsState>(EMPTY_STATE);
  const [configOpen, setConfigOpen] = useState(false);
  const [drillView, setDrillView] = useState<DrillView>('main');

  // Reconcile fetched connectors into local state on data change so the
  // user's enabled-toggle state survives refreshes for ids that still
  // exist, while new connectors come in with their backend-derived
  // enabled flag (healthy => active by default).
  useEffect(() => {
    setState((prev) => {
      const prevById = new Map(prev.connectors.map((c) => [c.id, c]));
      return {
        ...prev,
        connectors: realConnectors.map((c) => {
          // Preserve the user's explicit toggle ONLY while the connector is
          // still genuinely connected; a connector that is no longer usable
          // (e.g. its token expired) can never stay enabled (bug #3).
          if (c.connectionState !== 'connected') return { ...c, enabled: false };
          return prevById.has(c.id) ? { ...c, enabled: prevById.get(c.id)!.enabled } : c;
        }),
      };
    });
  }, [realConnectors]);

  // ── PR-AAU-D1 hydration ────────────────────────────────────────────────
  //
  // When the parent passes a conversationId, fetch the persisted overrides
  // (or the resolved agent defaults) once. We *only* re-hydrate when the
  // id changes — toggle-driven re-renders must NOT re-trigger this, or
  // the user's optimistic toggle would flicker back to the server state
  // before the PATCH lands.
  const lastHydratedConvIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!conversationId) return;
    if (lastHydratedConvIdRef.current === conversationId) return;
    lastHydratedConvIdRef.current = conversationId;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<AttachedToolsResponse>(
          `/conversations/${conversationId}/attached-tools`,
        );
        if (cancelled) return;
        setState((prev) => mergeWireIntoState(prev, res.attached));
      } catch (err) {
        // Non-fatal — the chat still works without saved overrides.
        // Don't toast here, this fires on every conversation switch and
        // the spam would be obnoxious.
        // eslint-disable-next-line no-console
        console.warn('[PR-AAU-D1] failed to hydrate attached-tools', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversationId]);

  // ── PR-AAU-D1 persistence ──────────────────────────────────────────────
  //
  // Each toggle calls `persistDebounced(nextState)`. The 200ms debounce
  // collapses rapid clicks into a single PATCH. On error we toast +
  // revert to the snapshot taken at the moment the toggle fired (so
  // the user sees their attempted change undone, not an arbitrary older
  // state if other toggles fired in the same window).
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingPatchRef = useRef<{ snapshot: ChatToolsState; target: ChatToolsState } | null>(null);

  const flushPatch = useCallback(async () => {
    if (!conversationId) {
      pendingPatchRef.current = null;
      return;
    }
    const job = pendingPatchRef.current;
    if (!job) return;
    pendingPatchRef.current = null;
    try {
      await apiClient.patch(`/conversations/${conversationId}/attached-tools`, {
        attached: stateToWire(job.target),
      });
    } catch (err) {
      // Revert to the snapshot taken BEFORE the optimistic toggle.
      setState(job.snapshot);
      toast.error('Could not save chat tools — change reverted');
      // eslint-disable-next-line no-console
      console.warn('[PR-AAU-D1] PATCH attached-tools failed', err);
    }
  }, [conversationId]);

  const persistDebounced = useCallback(
    (snapshot: ChatToolsState, target: ChatToolsState) => {
      if (!conversationId) return; // brand-new chat — defer until first send
      // Always overwrite snapshot/target — debounce window can collapse
      // multiple toggles into one PATCH. The snapshot we keep is the
      // STATE BEFORE the FIRST toggle in the window, so a single revert
      // undoes the whole batch.
      if (pendingPatchRef.current) {
        pendingPatchRef.current = {
          snapshot: pendingPatchRef.current.snapshot,
          target,
        };
      } else {
        pendingPatchRef.current = { snapshot, target };
      }
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        void flushPatch();
      }, 200);
    },
    [conversationId, flushPatch],
  );

  // Flush any pending PATCH on unmount so a fast nav doesn't drop the
  // user's last toggle.
  useEffect(() => {
    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current);
        // Fire-and-forget; we're unmounting anyway.
        void flushPatch();
      }
    };
  }, [flushPatch]);

  // Toggle helpers — capture the pre-toggle snapshot for revert.
  const toggleWebSearch = () => {
    setState((prev) => {
      const next = { ...prev, webSearch: !prev.webSearch };
      persistDebounced(prev, next);
      return next;
    });
  };
  const toggleConnector = (id: string) => {
    setState((prev) => {
      const next = {
        ...prev,
        connectors: prev.connectors.map((c) =>
          c.id === id ? { ...c, enabled: !c.enabled } : c,
        ),
      };
      persistDebounced(prev, next);
      return next;
    });
  };
  const toggleAgent = (id: string) => {
    setState((prev) => {
      const next = {
        ...prev,
        agents: prev.agents.map((a) =>
          a.id === id ? { ...a, enabled: !a.enabled } : a,
        ),
      };
      persistDebounced(prev, next);
      return next;
    });
  };
  const toggleSkill = (id: string) => {
    setState((prev) => {
      const next = {
        ...prev,
        skills: prev.skills.map((s) =>
          s.id === id ? { ...s, enabled: !s.enabled } : s,
        ),
      };
      persistDebounced(prev, next);
      return next;
    });
  };
  const resetDefaults = () => {
    setState((prev) => {
      const next: ChatToolsState = {
        webSearch: false,
        connectors: realConnectors,
        agents: [],
        skills: [],
      };
      persistDebounced(prev, next);
      return next;
    });
    setDrillView('main');
  };

  const activeConnectors = state.connectors.filter(c => c.enabled);
  const activeAgents = state.agents.filter(a => a.enabled);
  const activeSkills = state.skills.filter(s => s.enabled);

  // bug #8 — in TanStack Query v5 a DISABLED query reports isLoading=false, so
  // while auth is still hydrating (queries not yet enabled) we must treat the
  // strip as loading; otherwise hasNoConnectors flips true and flashes the
  // "Connect a system to enable tools" empty state before the real fetch runs.
  const isLoading = !authReady || mcpQuery.isLoading || apiQuery.isLoading;
  const hasNoConnectors =
    !isLoading && state.connectors.length === 0;

  // Reset drill view when popover closes
  const handleOpenChange = (open: boolean) => {
    setConfigOpen(open);
    if (!open) setDrillView('main');
  };

  return (
    <div className={cn('flex items-center gap-2 px-4 py-1.5 border-b border-border/20 bg-muted/20 shrink-0', className)}>
      {/* Active chips — scrollable */}
      <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto">
        {state.webSearch && (
          <Chip icon={<Globe className="h-3 w-3" />} label="Web Search" color="text-blue-500 bg-blue-500/10" tooltip="Search the web via DuckDuckGo/SearXNG" onRemove={toggleWebSearch} />
        )}
        {activeConnectors.map(c => (
          // bug #3 — clicking the chip body used to be a dead no-op. Open the
          // Connections page so the chip actually leads somewhere (manage /
          // inspect the connection); the × still detaches it from this chat.
          <Chip key={c.id} icon={<span className="text-[10px]">{c.icon}</span>} label={c.name} color="text-green-500 bg-green-500/10" tooltip={`${c.name} — ${c.toolCount} tools available · click to manage`} onRemove={() => toggleConnector(c.id)} onClick={() => navigate({ to: '/connections' })} />
        ))}
        {activeAgents.map(a => (
          <Chip key={a.id} icon={<span className="text-[10px]">{a.icon}</span>} label={a.name} color="text-orange-500 bg-orange-500/10" tooltip={`${a.name} — ${a.description}`} onRemove={() => toggleAgent(a.id)} />
        ))}
        {activeSkills.length > 0 && (
          <Chip icon={<Sparkles className="h-3 w-3" />} label={`${activeSkills.length} skill${activeSkills.length > 1 ? 's' : ''}`} color="text-purple-500 bg-purple-500/10" tooltip={`Active: ${activeSkills.map(s => s.name).join(', ')}`} />
        )}
        {!state.webSearch && activeConnectors.length === 0 && activeAgents.length === 0 && activeSkills.length === 0 && (
          hasNoConnectors ? (
            <span className="text-[11px] text-muted-foreground">
              Connect a system to enable tools —{' '}
              <Link to="/connections" className="underline hover:text-foreground">
                manage connections
              </Link>
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground">
              {isLoading ? 'Loading tools…' : 'No tools active'}
            </span>
          )
        )}
      </div>

      {/* Configure button + popover */}
      <Popover open={configOpen} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-[11px] text-muted-foreground gap-1 shrink-0">
            <Settings2 className="h-3 w-3" />
            Configure
            <ChevronDown className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-0" sideOffset={8}>
          {drillView === 'main' && (
            <MainView
              state={state}
              onToggleWebSearch={toggleWebSearch}
              onDrill={setDrillView}
              onReset={resetDefaults}
            />
          )}
          {drillView === 'connectors' && (
            <DrillPanel
              title="Connectors"
              onBack={() => setDrillView('main')}
              items={state.connectors.map(c => ({
                id: c.id,
                label: c.name,
                // bug #3 — a connector that isn't genuinely connected can't be
                // toggled on; show why (sign in / not tested) instead of tools.
                sublabel: c.connectionState === 'connected' ? `${c.toolCount} tools` : connectorReasonLabel(c.needsSetupReason),
                icon: c.icon,
                enabled: c.enabled,
                disabled: c.connectionState !== 'connected',
              }))}
              onToggle={toggleConnector}
            />
          )}
          {drillView === 'agents' && (
            <DrillPanel
              title="Agents"
              onBack={() => setDrillView('main')}
              items={state.agents.map(a => ({ id: a.id, label: a.name, sublabel: a.description, icon: a.icon, enabled: a.enabled }))}
              onToggle={toggleAgent}
            />
          )}
          {drillView === 'skills' && (
            <DrillPanel
              title="Skills"
              onBack={() => setDrillView('main')}
              items={state.skills.map(s => ({ id: s.id, label: s.name, enabled: s.enabled }))}
              onToggle={toggleSkill}
            />
          )}
        </PopoverContent>
      </Popover>

      {/* Right action slot (e.g., context panel toggle) */}
      {rightAction}
    </div>
  );
}

// ── Main View (Level 1) ─────────────────────────────────────────────────────

function MainView({ state, onToggleWebSearch, onDrill, onReset }: {
  state: ChatToolsState;
  onToggleWebSearch: () => void;
  onDrill: (view: DrillView) => void;
  onReset: () => void;
}) {
  const activeConnectors = state.connectors.filter(c => c.enabled).length;
  const activeAgents = state.agents.filter(a => a.enabled).length;
  const activeSkills = state.skills.filter(s => s.enabled).length;

  return (
    <div className="p-3">
      <p className="text-sm font-medium mb-3">Chat Tools</p>

      {/* Web search — inline toggle.
          #1079 — Codex Test 5 confirmed the UI sends a `toggles` payload
          but `/api/v1/ecosystem/query` does not read web-search flags.
          The toggle has no live effect today. We keep the control so
          tests/wiring can land it later, but label it "preview" so
          users do not infer a working web-backed answer path. */}
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-blue-500" />
          <span className="text-sm">Web Search</span>
          <span
            className="ml-1 text-[9px] uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-400 border border-amber-500/40 rounded px-1 py-px"
            title="The Ask Argus backend does not yet honor the web-search toggle — answers will not be web-grounded."
          >
            preview
          </span>
        </div>
        <Switch checked={state.webSearch} onCheckedChange={onToggleWebSearch} />
      </div>

      <Separator className="my-2" />

      {/* Category rows — click to drill */}
      <CategoryRow
        icon={<Plug className="h-4 w-4 text-green-500" />}
        label="Connectors"
        count={`${activeConnectors}/${state.connectors.length}`}
        onClick={() => onDrill('connectors')}
      />
      <CategoryRow
        icon={<Bot className="h-4 w-4 text-orange-500" />}
        label="Agents"
        count={`${activeAgents}/${state.agents.length}`}
        onClick={() => onDrill('agents')}
      />
      <CategoryRow
        icon={<Sparkles className="h-4 w-4 text-purple-500" />}
        label="Skills"
        count={`${activeSkills}/${state.skills.length}`}
        onClick={() => onDrill('skills')}
      />

      <Separator className="my-2" />

      <button onClick={onReset} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
        Reset to defaults
      </button>
    </div>
  );
}

function CategoryRow({ icon, label, count, onClick }: {
  icon: React.ReactNode; label: string; count: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-2 py-2.5 hover:bg-muted/50 rounded-md px-1 transition-colors"
    >
      {icon}
      <span className="text-sm flex-1 text-left">{label}</span>
      <span className="text-xs text-muted-foreground">{count}</span>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
    </button>
  );
}

// ── Drill Panel (Level 2) ───────────────────────────────────────────────────

// bug #3 — friendly reason text for a connector that isn't usable yet.
function connectorReasonLabel(reason: string): string {
  switch (reason) {
    case 'needs_signin': return 'Sign in to use';
    case 'token_expired': return 'Session expired — sign in again';
    case 'never_tested': return 'Not tested yet — test in Connections';
    case 'degraded': return 'Connection degraded';
    default: return 'Needs setup';
  }
}

function DrillPanel({ title, onBack, items, onToggle }: {
  title: string;
  onBack: () => void;
  items: { id: string; label: string; sublabel?: string; icon?: string; enabled: boolean; disabled?: boolean }[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="p-3">
      {/* Back header */}
      <button
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm font-medium mb-3 text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        {title}
      </button>

      {/* Item list */}
      <div className="space-y-1">
        {items.map(item => (
          <div key={item.id} className="flex items-center justify-between py-2 px-1">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {item.icon && <span className="text-sm shrink-0">{item.icon}</span>}
              <div className="min-w-0">
                <p className="text-sm truncate">{item.label}</p>
                {item.sublabel && <p className="text-[10px] text-muted-foreground truncate">{item.sublabel}</p>}
              </div>
            </div>
            <Switch checked={item.enabled} disabled={item.disabled} onCheckedChange={() => onToggle(item.id)} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Chip ─────────────────────────────────────────────────────────────────────

function Chip({ icon, label, color, tooltip, onRemove, onClick }: {
  icon: React.ReactNode; label: string; color: string; tooltip?: string; onRemove?: () => void; onClick?: () => void;
}) {
  const badge = (
    <Badge
      variant="outline"
      className={cn('text-[10px] h-5 px-1.5 gap-1 shrink-0 font-normal', onClick && 'cursor-pointer', color)}
      onClick={onClick}
    >
      {icon}
      {label}
      {onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} className="ml-0.5 hover:text-foreground transition-colors">
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </Badge>
  );

  if (!tooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{badge}</TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs max-w-[200px]">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
